import { convertToFormData } from '@/utils/formDataConverter'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useRouter } from 'next/navigation'
import { DEFAULT_SUBACCOUNT_ID, DEFAULT_LANDING_ID } from '@/utils/constants'
import { resendOTP, verifySignUpOTP } from '@/lib/auth-service'
import { useSignupFormFirstStep } from '@/hooks/forms/useSignupForm'

export const OTPFormSchema = z.object({
  otp: z.string().length(6, { message: 'OTP must be exactly 6 characters' })
})

export type OTPFormValues = z.infer<typeof OTPFormSchema>

export const useOTPForm = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const router = useRouter()
  const { completeInviteSignup } = useSignupFormFirstStep()

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch
  } = useForm({
    resolver: zodResolver(OTPFormSchema)
  })

  const onSubmitVerifySignUpOTP = async (
    email: string,
    token: string,
    isInvitedUser: boolean,
    password?: string,
    onSuccess?: () => void
  ) => {
    setIsLoading(true)

    try {
      // console.log('Submitting OTP verification for:', email)
      const response = await verifySignUpOTP({ email, otp: token })

      if (response) {
        // console.log('OTP verification successful')
        // Store the user session data
        localStorage.setItem('user', JSON.stringify(response))

        // In the new flow, we use the callback to move to the next step

        // Legacy flow for backward compatibility
        // console.log('Using legacy flow navigation')
        // Check if user has complete agency setup
        if (response.success) {
          const subaccountId =
            response?.agencyUser?.Permissions[0]?.subAccountId ||
            DEFAULT_SUBACCOUNT_ID
          const landingId = response?.landingId || DEFAULT_LANDING_ID

          if (isInvitedUser) {
            try {
              const inviteResponse = await completeInviteSignup({
                email: email,
                isInviteUser: isInvitedUser,
                password: password
              })
              if (inviteResponse.success) {
                // Successfully completed invite signup
                setSuccessMessage('Invitation accepted successfully')
                // router.push(
                //   `/subaccount/${subaccountId}/landings/${landingId}/landings`
                // )
              } else {
                // Handle invite signup failure
                setError('otp', {
                  type: 'custom',
                  message:
                    inviteResponse.message || 'Failed to accept invitation'
                })
              }
            } catch (error) {
              console.error('Error completing invite signup:', error)
              setError('otp', {
                type: 'custom',
                message:
                  error instanceof Error && typeof error.message === 'string'
                    ? error.message
                    : 'Something went wrong while accepting invitation'
              })
            }
            // Invited users go to the subaccount landings path
          } else {
            // Regular users go to pricing
            router.push(`/agency/${response?.agencyUser?.agencyId}/pricing`)
          }
        } else {
          // User doesn't have complete setup yet, this shouldn't happen in legacy flow
          // but handle it gracefully
          console.warn(
            'User does not have complete agency setup after OTP verification'
          )
        }
        if (onSuccess) {
          setIsLoading(false)
          // console.log('Calling onSuccess callback')
          onSuccess()
          return { ...response, success: true }
        }
      }

      setIsLoading(false)
      return { ...response, success: true }
    } catch (error) {
      console.error('OTP verification failed:', error)
      setIsLoading(false)
      const errorMessage =
        error instanceof Error && typeof error.message === 'string'
          ? error.message
          : 'Something went wrong'
      setError('otp', { type: 'custom', message: errorMessage })
      return { success: false }
    }
  }

  const resendSignupOTP = async (email: string) => {
    setIsLoading(true)
    setSuccessMessage('')
    const formData = convertToFormData({ email })

    try {
      const response = await resendOTP(email)
      if (response) {
        setSuccessMessage('OTP sent successfully')
      }
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setSuccessMessage('')
      setError('otp', {
        type: 'custom',
        message:
          error instanceof Error && typeof error.message === 'string'
            ? error.message
            : 'Something went wrong'
      })
    }
  }

  return {
    isLoading,
    errors,
    register,
    handleSubmit,
    onSubmitVerifySignUpOTP,
    resendSignupOTP,
    reset,
    successMessage,
    watch
  }
}
